/**
 * React Query hooks for the deck editor. UI modules should consume these
 * — never call the ``./api`` module directly from a component.
 *
 * Query key conventions:
 *   ["decks", "by-project", projectId]   — list view
 *   ["decks", "by-id", deckId]            — full editor view
 *   ["decks", "all-projects-decks"]       — aggregate list page (cross-project)
 */

import {
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { apiClient } from "../../api/client";
import {
  createDeck,
  deleteDeck,
  deleteSlide,
  fetchDeck,
  insertSlide,
  listDecksForProject,
  reorderSlides,
  updateDeckMeta,
  updateSlide,
} from "./api";
import type {
  Deck,
  DeckCreatePayload,
  DeckMetaUpdatePayload,
  DeckSummary,
  SlideInsertPayload,
  SlideInsertResult,
  SlideReorderPayload,
  SlideUpdatePayload,
} from "./types";

// ── Project list (used for cross-project aggregation) ──────────────────────

interface ProjectSummary {
  id: string;
  name: string;
  description?: string | null;
}

export function useUserProjects(): UseQueryReturn<ProjectSummary[]> {
  return useQuery<ProjectSummary[]>({
    queryKey: ["decks", "user-projects"],
    queryFn: () => apiClient<ProjectSummary[]>("/projects"),
    staleTime: 60_000,
  });
}

// react-query 5's return type is verbose; alias for brevity in this file.
type UseQueryReturn<T> = ReturnType<typeof useQuery<T>>;

// ── Per-project decks ──────────────────────────────────────────────────────

export function useDecksForProject(
  projectId: string | undefined,
  options?: Omit<UseQueryOptions<DeckSummary[]>, "queryKey" | "queryFn">,
): UseQueryReturn<DeckSummary[]> {
  return useQuery<DeckSummary[]>({
    queryKey: ["decks", "by-project", projectId],
    queryFn: () => listDecksForProject(projectId as string),
    enabled: Boolean(projectId),
    ...options,
  });
}

/**
 * Fan-out: list decks across every project the user can see. Used by the
 * top-level ``/decks`` listing page so engineers can scan everything at
 * once without first picking a project.
 *
 * Implementation: get the user's projects, then issue parallel calls per
 * project via ``useQueries``. Each per-project failure is isolated so a
 * 403 on one project does not blank out the list.
 */
export function useAllProjectDecks() {
  const projects = useUserProjects();

  const perProject = useQueries({
    queries: (projects.data ?? []).map((p) => ({
      queryKey: ["decks", "by-project", p.id],
      queryFn: () => listDecksForProject(p.id),
      staleTime: 30_000,
      // Tolerate per-project failures — most likely "no membership" on a
      // public project the API still surfaces.
      retry: 0,
    })),
  });

  const groups = (projects.data ?? []).map((project, idx) => {
    const r = perProject[idx];
    return {
      project_id: project.id,
      project_name: project.name,
      decks: (r?.data ?? []) as DeckSummary[],
      isLoading: r?.isLoading ?? false,
      error: r?.error ?? null,
    };
  });

  return {
    projects,
    groups,
    isLoading: projects.isLoading || perProject.some((r) => r.isLoading),
    isError: projects.isError,
    error: projects.error,
  };
}

// ── Single deck (editor view) ──────────────────────────────────────────────

export function useDeck(
  deckId: string | undefined,
  options?: Omit<UseQueryOptions<Deck>, "queryKey" | "queryFn">,
): UseQueryReturn<Deck> {
  return useQuery<Deck>({
    queryKey: ["decks", "by-id", deckId],
    queryFn: () => fetchDeck(deckId as string),
    enabled: Boolean(deckId),
    ...options,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

export function useCreateDeck(
  projectId: string,
  options?: UseMutationOptions<Deck, Error, DeckCreatePayload>,
) {
  const qc = useQueryClient();
  return useMutation<Deck, Error, DeckCreatePayload>({
    mutationFn: (payload) => createDeck(projectId, payload),
    onSuccess: (deck) => {
      qc.invalidateQueries({ queryKey: ["decks", "by-project", projectId] });
      qc.setQueryData(["decks", "by-id", deck.id], deck);
    },
    ...options,
  });
}

export function useUpdateDeckMeta(
  deckId: string,
  options?: UseMutationOptions<
    Deck,
    Error,
    { payload: DeckMetaUpdatePayload; ifMatchVersion?: number }
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    Deck,
    Error,
    { payload: DeckMetaUpdatePayload; ifMatchVersion?: number }
  >({
    mutationFn: ({ payload, ifMatchVersion }) =>
      updateDeckMeta(deckId, payload, ifMatchVersion),
    onSuccess: (deck) => {
      qc.setQueryData(["decks", "by-id", deckId], deck);
      qc.invalidateQueries({
        queryKey: ["decks", "by-project", deck.project_id],
      });
    },
    ...options,
  });
}

export function useUpdateSlide(
  deckId: string,
  options?: UseMutationOptions<
    Deck,
    Error,
    {
      slideIdx: number;
      payload: SlideUpdatePayload;
      ifMatchVersion?: number;
    }
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    Deck,
    Error,
    {
      slideIdx: number;
      payload: SlideUpdatePayload;
      ifMatchVersion?: number;
    }
  >({
    mutationFn: ({ slideIdx, payload, ifMatchVersion }) =>
      updateSlide(deckId, slideIdx, payload, ifMatchVersion),
    onSuccess: (deck) => {
      qc.setQueryData(["decks", "by-id", deckId], deck);
    },
    ...options,
  });
}

export function useDeleteDeck(
  deckId: string,
  options?: UseMutationOptions<void, Error, void>,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => deleteDeck(deckId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decks"] });
    },
    ...options,
  });
}

// ── Slide management mutations (PR3) ───────────────────────────────────────

export function useInsertSlide(
  deckId: string,
  options?: UseMutationOptions<
    SlideInsertResult,
    Error,
    { payload: SlideInsertPayload; ifMatchVersion?: number }
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    SlideInsertResult,
    Error,
    { payload: SlideInsertPayload; ifMatchVersion?: number }
  >({
    mutationFn: ({ payload, ifMatchVersion }) =>
      insertSlide(deckId, payload, ifMatchVersion),
    onSuccess: ({ deck }) => {
      qc.setQueryData(["decks", "by-id", deckId], deck);
      qc.invalidateQueries({
        queryKey: ["decks", "by-project", deck.project_id],
      });
    },
    ...options,
  });
}

export function useDeleteSlide(
  deckId: string,
  options?: UseMutationOptions<
    Deck,
    Error,
    { slideIdx: number; ifMatchVersion?: number }
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    Deck,
    Error,
    { slideIdx: number; ifMatchVersion?: number }
  >({
    mutationFn: ({ slideIdx, ifMatchVersion }) =>
      deleteSlide(deckId, slideIdx, ifMatchVersion),
    onSuccess: (deck) => {
      qc.setQueryData(["decks", "by-id", deckId], deck);
      qc.invalidateQueries({
        queryKey: ["decks", "by-project", deck.project_id],
      });
    },
    ...options,
  });
}

export function useReorderSlides(
  deckId: string,
  options?: UseMutationOptions<
    Deck,
    Error,
    { payload: SlideReorderPayload; ifMatchVersion?: number }
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    Deck,
    Error,
    { payload: SlideReorderPayload; ifMatchVersion?: number }
  >({
    mutationFn: ({ payload, ifMatchVersion }) =>
      reorderSlides(deckId, payload, ifMatchVersion),
    onSuccess: (deck) => {
      qc.setQueryData(["decks", "by-id", deckId], deck);
    },
    ...options,
  });
}
